'use strict';

(() => {
  // Initialize all top-level namespaces.
  window.mx = window.mx || {};

  window.mx.init = () => {
    // Initialize scrollbar width tracking.
    initScrollbarWidthTracking();

    // Initialize Gorgias integration.
    initGorgiasIntegration();

    window.dispatchEvent(new CustomEvent('mx:ready'));
  };

  window.mx.utils = window.mx.utils || {};
  window.mx.dom = window.mx.dom || {};
  window.mx.managers = window.mx.managers || {};
  window.mx.components = window.mx.components || {};

  /**
   * Sets the CSS variable --scrollbar-width to the width of the scrollbar.
   */
  window.mx.utils.setScrollbarWidthProperty = () => {
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', `${scrollBarWidth}px`);
  };

  /**
   * Gets the root font size of the document.
   * @returns {number} The root font size of the document.
   */
  window.mx.utils.getRootFontSize = () => {
    return parseFloat(getComputedStyle(document.documentElement).fontSize);
  };

  /**
   * Creates a throttled function that only invokes the provided callback function at most once per animation frame.
   * @param {Function} callback - The callback function to be throttled.
   * @returns {Function} - The throttled function.
   */
  window.mx.utils.throttle = (callback) => {
    let requestId = null;
    let lastArgs;

    const later = (context) => () => {
      requestId = null;
      callback.apply(context, lastArgs);
    };

    const throttled = (...args) => {
      lastArgs = args;
      if (requestId === null) {
        requestId = requestAnimationFrame(later(this));
      }
    };

    throttled.cancel = () => {
      cancelAnimationFrame(requestId);
      requestId = null;
    };

    return throttled;
  };

  /**
   * Creates a debounced function that delays invoking the provided callback function.
   * @param {Function} callback - The callback function to be debounced.
   * @param {number} [delay=100] - The delay in milliseconds before invoking the callback function.
   * @returns {Function} - The debounced function.
   */
  window.mx.utils.debounce = (callback, delay = 100) => {
    let timer = null;

    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        callback.apply(this, args);
      }, delay);
    };
  };

  /**
   * Utility functions for managing scroll locking and restoring scroll position.
   */
  window.mx.utils.scroll = {
    /**
     * Returns whether the document is currently locked.
     * @returns {boolean} - Whether the document is locked.
     */
    get isLocked() {
      return document.documentElement.hasAttribute('data-scroll-lock');
    },

    /**
     * Locks scrolling on the provided element and saves the current scroll position.
     * @param {HTMLElement} [element=document.documentElement] - The element to lock scrolling on.
     */
    lock: (element = document.documentElement) => {
      element.style.scrollBehavior = 'auto';
      element.style.top = `-${window.scrollY}px`;
      element.setAttribute('data-scroll-lock', '');
    },

    /**
     * Unlocks scrolling on the provided element and restores the previous scroll position.
     * @param {HTMLElement} [element=document.documentElement] - The element to unlock scrolling on.
     */
    unlock: (element = document.documentElement) => {
      element.removeAttribute('data-scroll-lock');
      window.scrollTo(0, -1 * parseInt(element.style.top));
      element.style.top = null;
      element.style.scrollBehavior = null;
    },
  };

  /**
   * Manages interface with the Gorgias third-party chat widget.
   */
  window.mx.utils.gorgias = {
    /**
     * Gets the Gorgias chat container element.
     * @returns {HTMLElement|null} The Gorgias chat container or null if not found.
     */
    getContainer: () => {
      try {
        const container = document.querySelector('#gorgias-chat-container');
        if (!container) {
          console.warn('Gorgias container not found');
          return null;
        }

        return container;
      } catch (error) {
        console.warn('Error getting Gorgias container:', error);
        return null;
      }
    },

    /**
     * Opens the Gorgias chat widget.
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    open: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      window.mx.utils.gorgias.lower(container);
      window.mx.utils.gorgias.show(container);

      window.GorgiasChat?.open();
    },

    /**
     * Closes the Gorgias chat widget.
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    close: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      window.mx.utils.gorgias.lower(container);
      window.mx.utils.gorgias.show(container);

      window.GorgiasChat?.close();
    },

    /**
     * Hides the Gorgias chat widget.
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    hide: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      container.setAttribute('data-hidden', '');
      window.GorgiasChat?.close();
    },

    /**
     * Shows the Gorgias chat widget.
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    show: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      container.removeAttribute('data-hidden');
    },

    /**
     * Elevates the Gorgias chat widget (increases z-index).
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    elevate: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      container.setAttribute('data-elevated', '');
    },

    /**
     * Lowers the Gorgias chat widget (restores normal z-index).
     * @param {HTMLElement} [container] - The Gorgias container to use (fetched automatically if not provided).
     */
    lower: (container) => {
      if (!container) {
        container = window.mx.utils.gorgias.getContainer();
        if (!container) return;
      }

      container.removeAttribute('data-elevated');
    },
  };

  /**
   * Manages DOM elements for easy access.
   */
  window.mx.dom = {
    get header() {
      return document.getElementById('header');
    },
    get footer() {
      return document.getElementById('footer');
    },
    get cartDrawer() {
      return document.getElementById('cart_drawer');
    },
    get modalManager() {
      return document.getElementById('modal_manager');
    },
    get stickyManager() {
      return document.getElementById('sticky_manager');
    },
  };

  /**
   * Manages event listeners for easy addition and removal.
   * Provides centralized tracking of events to prevent memory leaks.
   */
  window.mx.managers.EventsManager = class {
    constructor() {
      this._listeners = new Map();
    }

    /**
     * Adds an event listener and manages it for easy removal.
     * @param {EventTarget} element - The element to attach the event listener to.
     * @param {string} type - The type of event to listen for.
     * @param {Function} handler - The function to be called when the event occurs.
     * @param {Object|boolean} [options={}] - Options for the event listener.
     * @returns {this} - For method chaining.
     */
    add = (element, type, handler, options = {}) => {
      try {
        if (!(element instanceof EventTarget)) {
          throw new Error('Invalid element provided to add');
        }

        element.addEventListener(type, handler, options);

        if (!this._listeners.has(element)) {
          this._listeners.set(element, new Set());
        }
        this._listeners.get(element).add({ type, handler, options });

        return this;
      } catch (error) {
        console.error(error.message, this);
        return this;
      }
    };

    /**
     * Checks if a specific event listener exists.
     * @param {EventTarget} element - The element to check.
     * @param {string} type - The type of event.
     * @param {Function} handler - The handler function.
     * @returns {boolean} - Whether the listener exists.
     */
    has = (element, type, handler) => {
      if (!this._listeners.has(element)) return false;

      const listeners = this._listeners.get(element);
      return Array.from(listeners).some((listener) => listener.type === type && listener.handler === handler);
    };

    /**
     * Removes a listener for the event.
     * @param {EventTarget} element - The element to remove the listener from.
     * @param {string} type - The type of event.
     * @param {Function} handler - The handler function to remove.
     * @returns {this} - For method chaining.
     */
    remove = (element, type, handler) => {
      if (!this._listeners.has(element)) return this;

      const elementListeners = this._listeners.get(element);

      elementListeners.forEach((listener) => {
        if (listener.type === type && listener.handler === handler) {
          element.removeEventListener(type, handler, listener.options);
          elementListeners.delete(listener);
        }
      });

      if (elementListeners.size === 0) {
        this._listeners.delete(element);
      }

      return this;
    };

    /**
     * Removes all managed event listeners.
     * @returns {this} - For method chaining.
     */
    clear = () => {
      for (const [element, listeners] of this._listeners) {
        listeners.forEach(({ type, handler, options }) => {
          element.removeEventListener(type, handler, options);
        });
      }
      this._listeners.clear();

      return this;
    };

    /**
     * Gets the count of managed listeners.
     * @returns {number} - The total number of managed listeners.
     */
    getCount = () => {
      let count = 0;
      for (const listeners of this._listeners.values()) {
        count += listeners.size;
      }
      return count;
    };
  };

  /**
   * Manages observers (Resize, Intersection, Mutation) for easy addition and cleanup.
   * Provides centralized tracking of observers to prevent memory leaks.
   */
  window.mx.managers.ObserversManager = class {
    constructor() {
      this._observers = new Map();
    }

    /**
     * Creates and adds an observer for the specified element.
     * @param {Element} element - The element to observe.
     * @param {string} type - The type of observer ('resize', 'intersection', or 'mutation').
     * @param {Function} handler - The function to be called when changes are observed.
     * @param {Object} [options={}] - Options for the observer.
     * @returns {ResizeObserver|IntersectionObserver|MutationObserver|undefined} - The created observer.
     */
    add = (element, type, handler, options = {}) => {
      try {
        if (!(element instanceof Element)) {
          throw new Error('Invalid element provided to add');
        } else if (!['resize', 'intersection', 'mutation'].includes(type)) {
          throw new Error('Invalid observer type provided');
        }

        let observer;

        switch (type) {
          case 'resize':
            observer = new ResizeObserver(handler);
            observer.observe(element, options);
            break;
          case 'intersection':
            observer = new IntersectionObserver(handler, options);
            observer.observe(element);
            break;
          case 'mutation':
            observer = new MutationObserver(handler);
            observer.observe(element, options);
            break;
        }

        if (!this._observers.has(element)) {
          this._observers.set(element, new Set());
        }

        this._observers.get(element).add({ type, handler, observer });

        return observer;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    };

    /**
     * Convenience method to add a resize observer.
     * @param {Element} element - The element to observe resize events on.
     * @param {Function} handler - The function to be called when resize occurs.
     * @param {Object} [options={}] - Options for the resize observer.
     * @returns {ResizeObserver|undefined} - The created observer.
     */
    resize = (element, handler, options = {}) => {
      return this.add(element, 'resize', handler, options);
    };

    /**
     * Convenience method to add an intersection observer.
     * @param {Element} element - The element to observe intersection events on.
     * @param {Function} handler - The function to be called when intersection changes.
     * @param {Object} [options={}] - Options for the intersection observer.
     * @returns {IntersectionObserver|undefined} - The created observer.
     */
    intersection = (element, handler, options = {}) => {
      return this.add(element, 'intersection', handler, options);
    };

    /**
     * Convenience method to add a mutation observer.
     * @param {Element} element - The element to observe mutations on.
     * @param {Function} handler - The function to be called when mutations occur.
     * @param {Object} [options={}] - Options for the mutation observer.
     * @returns {MutationObserver|undefined} - The created observer.
     */
    mutation = (element, handler, options = {}) => {
      return this.add(element, 'mutation', handler, options);
    };

    /**
     * Checks if a specific observer exists.
     * @param {Element} element - The element to check.
     * @param {string} type - The type of observer.
     * @param {Function} handler - The handler function.
     * @returns {boolean} - Whether the observer exists.
     */
    has = (element, type, handler) => {
      if (!this._observers.has(element)) return false;

      const observers = this._observers.get(element);
      return Array.from(observers).some((entry) => entry.type === type && entry.handler === handler);
    };

    /**
     * Removes a specific observer.
     * @param {Element} element - The element to remove the observer from.
     * @param {string} type - The type of observer to remove.
     * @param {Function} handler - The handler function to remove.
     * @returns {this} - For method chaining.
     */
    remove = (element, type, handler) => {
      const elementObservers = this._observers.get(element);
      if (!elementObservers) return this;

      elementObservers.forEach((entry) => {
        if (entry.type === type && entry.handler === handler) {
          entry.observer.disconnect();
          elementObservers.delete(entry);
        }
      });

      if (elementObservers.size === 0) {
        this._observers.delete(element);
      }

      return this;
    };

    /**
     * Removes all observers for a specific element.
     * @param {Element} element - The element to remove all observers from.
     * @returns {this} - For method chaining.
     */
    removeFrom = (element) => {
      const elementObservers = this._observers.get(element);
      if (!elementObservers) return this;

      elementObservers.forEach(({ observer }) => {
        observer.disconnect();
      });

      this._observers.delete(element);
      return this;
    };

    /**
     * Removes all observers.
     * @returns {this} - For method chaining.
     */
    clear = () => {
      for (const observers of this._observers.values()) {
        observers.forEach(({ observer }) => {
          observer.disconnect();
        });
      }

      this._observers.clear();
      return this;
    };

    /**
     * Gets the count of managed observers.
     * @returns {number} - The total number of managed observers.
     */
    getCount = () => {
      let count = 0;
      for (const observers of this._observers.values()) {
        count += observers.size;
      }
      return count;
    };
  };

  /**
   * Manages caching of values for improved performance.
   * Provides a flexible, type-based caching system with selective invalidation.
   */
  window.mx.managers.CacheManager = class {
    constructor() {
      this.caches = new Map();
      this.caches.set('dom', new Map());
    }

    /**
     * Gets a cached value or computes and caches it if not present.
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @param {Function} computeFunction - Function to compute the value if not cached.
     * @returns {*} - The cached or computed value.
     */
    get = (type, key, computeFunction) => {
      const cache = this.ensureCache(type);

      if (!cache.has(key)) {
        cache.set(key, computeFunction());
      }

      return cache.get(key);
    };

    /**
     * Sets a value in the cache.
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @param {*} value - The value to cache.
     * @returns {*} - The cached value.
     */
    set = (type, key, value) => {
      const cache = this.ensureCache(type);
      cache.set(key, value);
      return value;
    };

    /**
     * Checks if a value exists in the cache.
     * @param {string} type - The cache type.
     * @param {string} key - The cache key.
     * @returns {boolean} - Whether the value is cached.
     */
    has = (type, key) => {
      if (!this.caches.has(type)) return false;
      return this.caches.get(type).has(key);
    };

    /**
     * Clears specific cache types or all caches.
     * @param {string|string[]} [types] - The cache types to clear. If omitted, clears all caches.
     * @returns {this} - For method chaining.
     */
    clear = (types) => {
      if (types) {
        [].concat(types).forEach((type) => {
          if (this.caches.has(type)) {
            this.caches.get(type).clear();
          }
        });
      } else {
        this.caches.forEach((cache) => cache.clear());
      }

      return this;
    };

    /**
     * Ensures a cache exists for the given type and returns it.
     * @param {string} type - The cache type.
     * @returns {Map<string, any>} - The cache Map.
     */
    ensureCache = (type) => {
      if (!this.caches.has(type)) {
        this.caches.set(type, new Map());
      }
      return this.caches.get(type);
    };

    /**
     * Gets all available cache types.
     * @returns {string[]} - Array of cache type names.
     */
    getTypes = () => {
      return Array.from(this.caches.keys());
    };

    /**
     * Removes cache entries that match a predicate function.
     * @param {string|string[]} types - The cache types to check.
     * @param {Function} predicate - Function that returns true for entries to remove.
     * @returns {number} - Number of entries removed.
     */
    removeIf = (types, predicate) => {
      let removedCount = 0;
      const typeList = [].concat(types);

      typeList.forEach((type) => {
        if (!this.caches.has(type)) return;

        const cache = this.caches.get(type);
        const entriesForRemoval = [];

        // Find entries to remove
        for (const [key, value] of cache.entries()) {
          if (predicate(value, key, type)) {
            entriesForRemoval.push(key);
          }
        }

        // Remove the entries
        entriesForRemoval.forEach((key) => {
          cache.delete(key);
          removedCount++;
        });
      });

      return removedCount;
    };
  };

  /**
   * Manages attribute-based modifiers for HTML elements.
   * Provides methods to check, add, remove, and toggle modifiers in a data attribute.
   */
  window.mx.managers.ModifiersManager = class {
    constructor(element) {
      this.element = element;
      this.attributeName = 'data-modifiers';
    }

    /**
     * Gets the list of all modifiers on the element
     * @returns {string[]} Array of modifier strings
     */
    list = () => {
      if (!this.element.hasAttribute(this.attributeName)) return [];

      return this.element
        .getAttribute(this.attributeName)
        .split(',')
        .map((modifier) => modifier.trim())
        .filter(Boolean); // Filter out empty strings
    };

    /**
     * Checks if the element has a specific modifier
     * @param {string} modifier - The modifier to check for
     * @returns {boolean} True if the element has the modifier
     */
    has = (modifier) => {
      return this.list().includes(modifier);
    };

    /**
     * Checks if the element has any of the specified modifiers
     * @param {...string} modifiers - Modifiers to check for
     * @returns {boolean} True if the element has any of the modifiers
     */
    hasAny = (...modifiers) => {
      const currentModifiers = this.list();
      return modifiers.some((mod) => currentModifiers.includes(mod));
    };

    /**
     * Adds one or more modifiers to the element
     * @param {...string} modifiers - Modifiers to add
     * @returns {this} For method chaining
     */
    add = (...modifiers) => {
      const current = this.list();
      const newModifiers = modifiers.filter((m) => !current.includes(m));

      if (newModifiers.length === 0) return this;

      const updated = [...current, ...newModifiers];
      this.element.setAttribute(this.attributeName, updated.join(', '));

      return this;
    };

    /**
     * Removes one or more modifiers from the element
     * @param {...string} modifiers - Modifiers to remove
     * @returns {this} For method chaining
     */
    remove = (...modifiers) => {
      const current = this.list();
      const updated = current.filter((m) => !modifiers.includes(m));

      if (updated.length === 0) {
        this.element.removeAttribute(this.attributeName);
      } else {
        this.element.setAttribute(this.attributeName, updated.join(', '));
      }

      return this;
    };

    /**
     * Toggles one or more modifiers on the element
     * @param {...string} modifiers - Modifiers to toggle
     * @returns {this} For method chaining
     */
    toggle = (...modifiers) => {
      const current = this.list();
      const toAdd = [];
      const toRemove = [];

      // Separate modifiers into those to add and those to remove
      modifiers.forEach((modifier) => {
        if (current.includes(modifier)) {
          toRemove.push(modifier);
        } else {
          toAdd.push(modifier);
        }
      });

      // Apply changes in a single operation if possible
      if (toRemove.length > 0) {
        const remaining = current.filter((m) => !toRemove.includes(m));
        const updated = [...remaining, ...toAdd];

        if (updated.length === 0) {
          this.element.removeAttribute(this.attributeName);
        } else {
          this.element.setAttribute(this.attributeName, updated.join(', '));
        }
      } else if (toAdd.length > 0) {
        this.element.setAttribute(this.attributeName, [...current, ...toAdd].join(', '));
      }

      return this;
    };

    /**
     * Sets a single modifier based on a condition
     * @param {string} modifier - The modifier to set
     * @param {boolean} condition - Whether to add (true) or remove (false) the modifier
     * @returns {this} For method chaining
     */
    set = (modifier, condition) => {
      return condition ? this.add(modifier) : this.remove(modifier);
    };

    /**
     * Replaces one modifier with another
     * @param {string} oldModifier - The modifier to replace
     * @param {string} newModifier - The replacement modifier
     * @returns {this} For method chaining
     */
    replace = (oldModifier, newModifier) => {
      // Check if we actually need to make a change
      const hasOld = this.has(oldModifier);
      const hasNew = this.has(newModifier);

      if (!hasOld && !hasNew) {
        return this;
      }

      if (hasOld && !hasNew) {
        const current = this.list();
        const updated = current.map((m) => (m === oldModifier ? newModifier : m));
        this.element.setAttribute(this.attributeName, updated.join(', '));
      } else if (hasOld) {
        // Both exist, remove old one
        this.remove(oldModifier);
      }

      return this;
    };

    /**
     * Clears all modifiers
     * @returns {this} For method chaining
     */
    clear = () => {
      this.element.removeAttribute(this.attributeName);
      return this;
    };
  };

  window.mx.components.MxComponent = class extends HTMLElement {
    constructor() {
      super();

      this.events = new window.mx.managers.EventsManager();
      this.observers = new window.mx.managers.ObserversManager();
      this.modifiers = new window.mx.managers.ModifiersManager(this);
      this.cache = new window.mx.managers.CacheManager();
    }

    disconnectedCallback() {
      this.events.clear();
      this.observers.clear();
      this.modifiers.clear();
      this.cache.clear();
    }

    get section() {
      return this.cache.get('dom', 'section', () => this.closest('.shopify-section'));
    }

    get header() {
      return this.cache.get('dom', 'header', () => {
        const element = window.mx.dom.header;
        if (!element) {
          console.warn('Header element not found in DOM');
        }
        return element;
      });
    }

    get footer() {
      return this.cache.get('dom', 'footer', () => {
        const element = window.mx.dom.footer;
        if (!element) {
          console.warn('Footer element not found in DOM');
        }
        return element;
      });
    }

    get cartDrawer() {
      return this.cache.get('dom', 'cartDrawer', () => {
        const element = window.mx.dom.cartDrawer;
        if (!element) {
          console.warn('Cart drawer element not found in DOM');
        }
        return element;
      });
    }

    get modalManager() {
      return this.cache.get('dom', 'modalManager', () => {
        const element = window.mx.dom.modalManager;
        if (!element) {
          console.warn('Modal manager element not found in DOM');
        }
        return element;
      });
    }

    get stickyManager() {
      return this.cache.get('dom', 'stickyManager', () => {
        const element = window.mx.dom.stickyManager;
        if (!element) {
          console.warn('Sticky manager element not found in DOM');
        }
        return element;
      });
    }

    /**
     * Gets the computed style value of a CSS property from an element.
     * @param {HTMLElement} element - The element to get the computed style value from.
     * @param {string} property - The CSS property to get the value of.
     * @param {Object} [options={}] - Options for getting the computed style.
     * @param {string} [options.alias=null] - Cache key to use instead of the auto-generated one.
     * @param {any} [options.default=null] - The default value to return if the property is not set.
     * @returns {string} The computed style value.
     */
    getComputedStyleValue = (element = this, property, options = {}) => {
      const alias = options.alias || null;
      const defaultValue = options.default || null;

      // Determine the cache key to use
      const cacheKey =
        alias ||
        (() => {
          const elementId =
            element === this
              ? 'root'
              : element.getAttribute('data-role') || element.id || element.tagName.toLowerCase();
          return `${property}-${elementId}`;
        })();

      // Get or compute the value using the determined cache key
      return this.cache.get('style', cacheKey, () => {
        const computedValue = getComputedStyle(element).getPropertyValue(property).trim();
        return computedValue || defaultValue;
      });
    };
  };
  if (!window.customElements.get('mx-section')) {
    window.customElements.define('mx-section', window.mx.components.MxComponent);
  }

  /**
   * Drawer component for slide-in content panels.
   * Handles opening, closing, and scroll locking during drawer interactions.
   */
  window.mx.components.MxDrawer = class extends window.mx.components.MxComponent {
    constructor() {
      super();
    }

    connectedCallback() {
      super.connectedCallback();

      // If there's an overlay, add a click listener to close the drawer
      if (this.overlay) {
        this.events.add(this.overlay, 'click', this.close);
      }

      // Listen for escape key to close drawer
      this.events.add(window, 'keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('data-open')) {
          this.close();
        }
      });
    }

    /**
     * Checks if the drawer is currently open.
     * @returns {boolean} True if the drawer is open, false otherwise.
     */
    get isOpen() {
      return this.modifiers.has('open');
    }

    /**
     * Sets whether the drawer is open.
     * @param {boolean} value True if the drawer should be open, false otherwise.
     */
    set isOpen(value) {
      value ? this.open() : this.close();
    }

    /**
     * Gets the overlay element associated with this drawer.
     * @returns {HTMLElement|null} The overlay element or null if not found.
     */
    get overlay() {
      return this.cache.get('dom', 'overlay', () => this.querySelector('[data-role="overlay"]'));
    }

    /**
     * Opens the drawer and locks page scrolling.
     * @returns {this} For method chaining.
     */
    open = () => {
      this.modifiers.add('open');
      window.mx.utils.scroll.lock();

      this.dispatchEvent(
        new CustomEvent('mx-drawer:open', {
          bubbles: true,
          detail: { drawer: this },
        }),
      );

      setTimeout(() => {
        const focusable = this.querySelector(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable) focusable.focus();
      }, 100);

      return this;
    };

    /**
     * Closes the drawer and unlocks page scrolling.
     * @returns {this} For method chaining.
     */
    close = () => {
      this.modifiers.remove('open');

      window.mx.utils.scroll.unlock();

      this.dispatchEvent(
        new CustomEvent('mx-drawer:close', {
          bubbles: true,
          detail: { drawer: this },
        }),
      );

      return this;
    };

    /**
     * Toggles the drawer between open and closed states.
     * @returns {this} For method chaining.
     */
    toggle = () => {
      return this.modifiers.has('open') ? this.close() : this.open();
    };
  };
  if (!window.customElements.get('mx-drawer')) {
    window.customElements.define('mx-drawer', window.mx.components.MxDrawer);
  }

  /**
   * Slider component for creating scrollable carousels with navigation controls.
   * Supports various alignment options, looping, drag interactions, and more.
   */
  window.mx.components.MxSlider = class extends window.mx.components.MxComponent {
    constructor() {
      super();

      this.activeIndex = 0;

      this.dragState = {
        isDragging: false,
        left: 0,
        top: 0,
        x: 0,
        y: 0,
        startTime: 0,
      };

      this.pendingScroll = false;

      this.nearInlineStart = true;
      this.nearInlineEnd = false;

      this.preventClick = false;
    }

    connectedCallback() {
      try {
        if (this.slideList.length === 0) {
          throw new Error('Slider must have at least one slide');
        }

        this.updateSliderLayout();
        this.setupEventListeners();

        this.initializeActiveSlide();

        this.initializeControls();

        if (this.modifiers.has('log-events')) {
          this.logEvents();
        }
      } catch (error) {
        console.error('Error initializing MX slider:', error, this);
      }
    }

    /* DOM getters */
    get slider() {
      return this.cache.get('dom', 'slider', () => {
        const element = this.querySelector('[data-role="slider"]');
        if (!element) {
          console.warn('Slider element not found in MxSlider component');
        }
        return element;
      });
    }

    get navigation() {
      return this.cache.get('dom', 'navigation', () => this.querySelector('[data-role="navigation"]'));
    }

    get controls() {
      return this.cache.get('dom', 'controls', () => this.querySelector('[data-role="controls"]'));
    }

    get previousButton() {
      return this.cache.get('dom', 'previousButton', () => {
        const controls = this.controls;
        return controls?.querySelector('[data-role="previous"]');
      });
    }

    get nextButton() {
      return this.cache.get('dom', 'nextButton', () => {
        const controls = this.controls;
        return controls?.querySelector('[data-role="next"]');
      });
    }

    /* Configuration getters */
    get drag() {
      return this.cache.get('config', 'drag', () => !this.modifiers.has('no-drag'));
    }

    get loop() {
      return this.cache.get('config', 'loop', () => this.modifiers.has('loop'));
    }

    get updateSource() {
      return this.cache.get('config', 'updateSource', () =>
        this.modifiers.has('update-on-change') ? 'change' : 'settle',
      );
    }

    get contentSize() {
      return this.cache.get('style', 'contentSize', () => {
        const value = this.getComputedStyleValue(this, '--slider-content-inline-size', {
          alias: 'contentSize',
        });

        if (value.endsWith('rem')) {
          return window.mx.utils.getRootFontSize() * parseFloat(value);
        } else if (value.endsWith('px')) {
          return parseFloat(value);
        }

        return 0;
      });
    }

    get initialSlideIndex() {
      return this.cache.get('config', 'initialSlideIndex', () => {
        return this.getComputedStyleValue(this, '--initial-slide-index', {
          alias: 'initialSlideIndex',
          default: 0,
        });
      });
    }

    /* Layout getters */
    get slideList() {
      return this.cache.get('layout', 'slideList', () => Array.from(this.slider?.children || []));
    }

    get navigationItemList() {
      return this.cache.get('layout', 'navigationItems', () => Array.from(this.navigation?.children || []));
    }

    get currentPaddings() {
      return this.cache.get('layout', 'currentPaddings', () => {
        return {
          start: parseFloat(
            this.getComputedStyleValue(this.slider, '--slider-padding-start', {
              alias: 'paddingStart',
              default: 0,
            }),
          ),
          end: parseFloat(
            this.getComputedStyleValue(this.slider, '--slider-padding-end', {
              alias: 'paddingEnd',
              default: 0,
            }),
          ),
        };
      });
    }

    get contentAreaWidth() {
      return this.cache.get('layout', 'contentAreaWidth', () => {
        const { start, end } = this.currentPaddings;
        return this.offsetWidth - start - end;
      });
    }

    get visualCenter() {
      return this.cache.get('layout', 'visualCenter', () => {
        return this.currentPaddings.start + this.contentAreaWidth / 2;
      });
    }

    get availableWidth() {
      return this.cache.get('layout', 'availableWidth', () => {
        const contentSize = this.contentSize;
        return contentSize ? Math.min(this.offsetWidth, contentSize) : this.offsetWidth;
      });
    }

    get isEdgeAligned() {
      return this.cache.get('layout', 'isEdgeAligned', () => {
        const alignment = this.alignment;
        const position = this.position;

        return (alignment === 'start' && position === 'left') || (alignment === 'end' && position === 'right');
      });
    }

    /* Style getters */
    get gap() {
      return this.cache.get('style', 'gap', () => {
        const value = this.getComputedStyleValue(this, '--slider-gap', {
          alias: 'gap',
        });
        const rootFontSize = window.mx.utils.getRootFontSize();

        if (value.startsWith('clamp')) {
          if (this.offsetWidth < 35 * rootFontSize) {
            return (
              rootFontSize *
              parseFloat(
                this.getComputedStyleValue(this, '--mobile-gutter', {
                  default: 0,
                }),
              )
            );
          } else {
            return (
              rootFontSize *
              parseFloat(
                this.getComputedStyleValue(this, '--desktop-gutter', {
                  default: 0,
                }),
              )
            );
          }
        } else if (value.endsWith('rem')) {
          return rootFontSize * parseFloat(value);
        } else if (value.endsWith('px')) {
          return parseFloat(value);
        }

        return 0;
      });
    }

    get sliderPadding() {
      return this.cache.get('style', 'sliderPadding', () => {
        const value = this.getComputedStyleValue(this, '--slider-padding', {
          alias: 'sliderPadding',
        });
        const rootFontSize = window.mx.utils.getRootFontSize();

        if (value.startsWith('clamp')) {
          if (this.offsetWidth < 35 * rootFontSize) {
            return (
              rootFontSize *
              parseFloat(
                this.getComputedStyleValue(this, '--mobile-gutter', {
                  default: 0,
                }),
              )
            );
          } else {
            return (
              rootFontSize *
              parseFloat(
                this.getComputedStyleValue(this, '--desktop-gutter', {
                  default: 0,
                }),
              )
            );
          }
        } else if (value.endsWith('rem')) {
          return rootFontSize * parseFloat(value);
        } else if (value.endsWith('px')) {
          return parseFloat(value);
        }

        return 0;
      });
    }

    get alignment() {
      return this.cache.get('style', 'alignment', () =>
        this.getComputedStyleValue(this, '--slider-alignment', {
          alias: 'alignment',
          default: 'start',
        }),
      );
    }

    get position() {
      const value = this.getComputedStyleValue(this, '--slider-position', {
        alias: 'position',
      });
      return ['left', 'right'].includes(value) ? value : 'none';
    }

    get slidesPerView() {
      return this.cache.get('style', 'slidesPerView', () => {
        const value = this.getComputedStyleValue(this, '--slides-per-view', {
          alias: 'slidesPerView',
        });

        if (value === 'auto') return null;

        const parsedValue = parseInt(value);
        return !isNaN(parsedValue) && parsedValue > 0 ? parsedValue : null;
      });
    }

    get scrollBehavior() {
      return this.cache.get('style', 'scrollBehavior', () =>
        this.getComputedStyleValue(this.slider, 'scroll-behavior', {
          alias: 'scrollBehavior',
        }) === 'smooth'
          ? 'smooth'
          : 'instant',
      );
    }

    setupEventListeners = () => {
      this.events.add(this.slider, 'scroll', window.mx.utils.throttle(this.onScroll), { passive: true });
      this.events.add(this.slider, 'scroll', window.mx.utils.debounce(this.onScrollEnd, 200), {
        passive: true,
      });

      this.observers.resize(this, this.onResize);
      this.events.add(this, 'keydown', this.onKeyDown);

      if (this.drag) {
        const mediaQuery = window.matchMedia('(pointer: fine)');
        this.events.add(mediaQuery, 'change', this.updateDragBehavior);
        this.updateDragBehavior(mediaQuery);
      }
    };

    initializeActiveSlide = () => {
      if (this.initialSlideIndex !== this.activeIndex) {
        this.select(this.initialSlideIndex, 'instant');
      } else {
        this.activeIndex = this.calculateClosestIndex();
        this.onScroll();
      }
    };

    initializeControls = () => {
      if (this.navigation) {
        this.navigationItemList.forEach((item, index) => {
          this.events.add(item, 'click', () => this.select(index));
        });
        this.updateNavigation();
      }

      if (this.controls) {
        if (this.previousButton) {
          this.events.add(this.previousButton, 'click', () => this.previous());
        }
        if (this.nextButton) {
          this.events.add(this.nextButton, 'click', () => this.next());
        }
        this.updateControls();
      }
    };

    logEvents = () => {
      const events = [
        'mx-slider:select',
        'mx-slider:previous',
        'mx-slider:next',
        'mx-slider:drag-start',
        'mx-slider:drag-end',
        'mx-slider:change',
        'mx-slider:settle',
        'mx-slider:edge-nearing',
        'mx-slider:edge-leaving',
      ];

      events.forEach((eventName) => {
        this.events.add(this, eventName, (event) => {
          console.log(`[MxSlider] ${eventName}:`, event.detail);
        });
      });
    };

    /**
     * Navigates to the previous slide
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    previous = (behavior = this.scrollBehavior) => {
      this.dispatchEvent(new CustomEvent('mx-slider:previous'));

      if (this.loop && this.nearInlineStart) {
        this.select(this.slideList.length - 1, behavior);
      } else {
        this.select(Math.max(this.activeIndex - 1, 0), behavior);
      }

      return this;
    };

    /**
     * Navigates to the next slide
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    next = (behavior = this.scrollBehavior) => {
      this.dispatchEvent(new CustomEvent('mx-slider:next'));

      if (this.loop && this.nearInlineEnd) {
        this.select(0, behavior);
      } else {
        this.select(Math.min(this.activeIndex + 1, this.slideList.length - 1), behavior);
      }

      return this;
    };

    /**
     * Selects a specific slide by index
     * @param {number} index - The slide index
     * @param {string} [behavior=this.scrollBehavior] - The scroll behavior
     * @returns {this} - For method chaining
     */
    select = (index, behavior = this.scrollBehavior) => {
      if (!this.slider || this.slider.offsetWidth === 0) return this;

      this.dispatchEvent(
        new CustomEvent('mx-slider:select', {
          detail: { slide: this.slideList[index], index },
        }),
      );

      this.activeIndex = index;
      this.pendingScroll = true;

      this.slider.scrollTo({
        left: this.calculateSlideTargetScroll(this.slideList[index]),
        behavior,
      });

      this.updateNavigation();

      return this;
    };

    updateActiveIndex = () => {
      const closestIndex = this.calculateClosestIndex();

      if (closestIndex !== this.activeIndex) {
        this.activeIndex = closestIndex;

        this.dispatchEvent(
          new CustomEvent('mx-slider:change', {
            detail: { slide: this.slideList[this.activeIndex], index: this.activeIndex },
          }),
        );

        if (this.updateSource === 'change') {
          this.updateNavigation();
        }
      }
    };

    updateSliderLayout = () => {
      this.cache.clear(['layout']);

      const slideWidth = this.calculateSlideWidth();

      const { primaryPadding, secondaryPadding } = this.calculatePaddings(slideWidth);

      if (this.alignment === 'end') {
        this.slider.style.setProperty('--slider-padding-start', `${secondaryPadding}px`);
        this.slider.style.setProperty('--slider-padding-end', `${primaryPadding}px`);
      } else {
        this.slider.style.setProperty('--slider-padding-start', `${primaryPadding}px`);
        this.slider.style.setProperty('--slider-padding-end', `${secondaryPadding}px`);
      }
    };

    calculateSlideWidth = () => {
      const slidesPerView = this.slidesPerView;
      let slideWidth;

      if (slidesPerView) {
        const availableWidth = this.availableWidth;
        slideWidth = (availableWidth - this.gap * (slidesPerView - 1) - this.sliderPadding * 2) / slidesPerView;
        this.slider.style.setProperty('--slide-inline-size', `${slideWidth}px`);
      } else {
        const lastSlideIndex = this.alignment === 'start' ? this.slideList.length - 1 : 0;

        slideWidth = this.slideList[lastSlideIndex]?.offsetWidth || 0;
        this.slider.style.removeProperty('--slide-inline-size');
      }

      return slideWidth;
    };

    calculatePaddings = (slideWidth) => {
      const alignment = this.alignment;
      const contentSize = this.contentSize;
      const sliderPadding = this.sliderPadding;

      const centerPadding = (this.offsetWidth - slideWidth) / 2;
      let contentGap = contentSize !== 0 ? this.offsetWidth - contentSize : 0;

      let primaryPadding, secondaryPadding;

      if (alignment === 'center') {
        primaryPadding = secondaryPadding = centerPadding;
      } else {
        primaryPadding = this.isEdgeAligned
          ? Math.max(sliderPadding, contentGap + sliderPadding)
          : Math.max(sliderPadding, contentGap / 2 + sliderPadding);

        secondaryPadding = Math.max(sliderPadding, 2 * centerPadding - primaryPadding);
      }

      if (this.modifiers.has('no-padding')) {
        primaryPadding = secondaryPadding = sliderPadding;
      }

      return { primaryPadding, secondaryPadding };
    };

    calculateSlideTargetScroll = (slide) => {
      if (!slide) return 0;

      const alignment = this.alignment;
      const position = this.position;
      const { start: paddingStart, end: paddingEnd } = this.currentPaddings;

      let targetScroll;

      if (alignment === 'center') {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;

        if (position === 'left' || position === 'right') {
          targetScroll = slideCenter - this.visualCenter;
        } else {
          targetScroll = slideCenter - this.offsetWidth / 2;
        }
      } else if (alignment === 'start') {
        targetScroll = slide.offsetLeft - paddingStart;
      } else {
        targetScroll = slide.offsetLeft + slide.offsetWidth - this.offsetWidth + paddingEnd;
      }

      return Math.max(0, Math.min(targetScroll, this.slider.scrollWidth - this.offsetWidth));
    };

calculateClosestIndex() {
  let minDistance = Infinity;
  let closest = 0;

  this.slideList.forEach((slide, index) => {
    const distance = Math.abs(slide.offsetLeft - this.slidesContainer.scrollLeft);
    console.log(`Slide ${index}: distance = ${distance}`);
    if (distance < minDistance) {
      minDistance = distance;
      closest = index;
    }
  });

  return closest;
}


    getScrollReferencePoint = () => {
      if (!this.slider) return 0;

      const alignment = this.alignment;
      const position = this.position;
      const scrollLeft = this.slider.scrollLeft;
      const { start: paddingStart, end: paddingEnd } = this.currentPaddings;

      if (alignment === 'center') {
        if (position === 'left' || position === 'right') {
          return scrollLeft + this.visualCenter;
        } else {
          return scrollLeft + this.offsetWidth / 2;
        }
      } else if (alignment === 'start') {
        return scrollLeft + paddingStart;
      }

      return scrollLeft + this.offsetWidth - paddingEnd;
    };

    getSlideReferencePoint = (slide, alignment = this.alignment) => {
      if (!slide) return 0;

      if (alignment === 'center') {
        return slide.offsetLeft + slide.offsetWidth / 2;
      } else if (alignment === 'start') {
        return slide.offsetLeft;
      } else {
        return slide.offsetLeft + slide.offsetWidth;
      }
    };

    updateNavigation = () => {
      if (!this.navigation) return;

      this.navigationItemList.forEach((item, index) => {
        item.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
      });

      const activeItem = this.navigationItemList[this.activeIndex];
      if (!activeItem) return;

      if (this.navigation.scrollWidth > this.navigation.clientWidth) {
        this.navigation.scrollTo({
          left: activeItem.offsetLeft - this.navigation.clientWidth / 2 + activeItem.offsetWidth / 2,
          behavior: this.scrollBehavior,
        });
      } else if (this.navigation.scrollHeight > this.navigation.clientHeight) {
        this.navigation.scrollTo({
          top: activeItem.offsetTop - this.navigation.clientHeight / 2 + activeItem.offsetHeight / 2,
          behavior: this.scrollBehavior,
        });
      }
    };

    updateControls = () => {
      if (!this.loop) {
        if (this.previousButton) {
          this.previousButton.disabled = this.nearInlineStart;
        }

        if (this.nextButton) {
          this.nextButton.disabled = this.nearInlineEnd;
        }
      } else {
        if (this.previousButton) {
          this.previousButton.disabled = false;
        }

        if (this.nextButton) {
          this.nextButton.disabled = false;
        }
      }
    };

    updateDragBehavior = (mediaQuery) => {
      const hasFinePointer = mediaQuery.matches || mediaQuery === true;

      if (hasFinePointer && this.drag) {
        this.events.add(this.slider, 'pointerdown', this.onPointerDown);
      } else {
        this.events.remove(this.slider, 'pointerdown', this.onPointerDown);
      }
    };

    onScroll = () => {
      if (!this.slider) return;

      const scrollLeft = this.slider.scrollLeft;
      const scrollWidth = this.slider.scrollWidth;
      const viewportWidth = this.slider.offsetWidth;
      const scrollEdgeThreshold = 50;

      this.checkEdgeState('start', scrollLeft, scrollEdgeThreshold);

      const distanceToEnd = scrollWidth - viewportWidth - scrollLeft;
      this.checkEdgeState('end', distanceToEnd, scrollEdgeThreshold);

      this.updateActiveIndex();
    };

    checkEdgeState = (edge, distance, threshold) => {
      const isStart = edge === 'start';
      const nearEdgeFlag = isStart ? 'nearInlineStart' : 'nearInlineEnd';

      if (distance < threshold && !this[nearEdgeFlag]) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-nearing', { detail: edge }));
        this[nearEdgeFlag] = true;
        this.updateControls();
      } else if (distance >= threshold && this[nearEdgeFlag]) {
        this.dispatchEvent(new CustomEvent('mx-slider:edge-leaving', { detail: edge }));
        this[nearEdgeFlag] = false;
        this.updateControls();
      }
    };

    onScrollEnd = () => {
      this.pendingScroll = false;

      this.dispatchEvent(
        new CustomEvent('mx-slider:settle', {
          detail: { slide: this.slideList[this.activeIndex], index: this.activeIndex },
        }),
      );

      if (this.updateSource === 'settle') {
        this.updateNavigation();
      }
    };

    onResize = () => {
      this.cache.clear(['config', 'layout', 'style']);

      this.updateSliderLayout();

      const currentIndex = this.calculateClosestIndex();

      if (currentIndex !== this.activeIndex) {
        this.select(currentIndex, 'instant');
      } else {
        this.select(this.activeIndex, 'instant');
      }
    };

    onKeyDown = (event) => {
      if (document.activeElement !== this && !this.contains(document.activeElement)) {
        return;
      }

      const keyActions = {
        ArrowLeft: () => this.previous(),
        ArrowUp: () => this.previous(),
        ArrowRight: () => this.next(),
        ArrowDown: () => this.next(),
        Home: () => this.select(0),
        End: () => this.select(this.slideList.length - 1),
      };

      const action = keyActions[event.key];
      if (action) {
        event.preventDefault();
        action();
      }
    };

    onPointerDown = (event) => {
      event.preventDefault();

      this.dragState = {
        isDragging: true,
        left: this.slider.scrollLeft,
        top: this.slider.scrollTop,
        x: event.clientX,
        y: event.clientY,
        startTime: Date.now(),
      };

      this.preventClick = false;

      this.dispatchEvent(new CustomEvent('mx-slider:drag-start'));

      this.events.add(document, 'pointermove', this.onPointerMove, { passive: false });
      this.events.add(document, 'pointerup', this.onPointerUp);

      this.events.add(this.slider, 'click', this.onClick, { capture: true });

      this.cache.clear('state');
    };

    onPointerMove = (event) => {
      if (!this.dragState.isDragging) return;

      event.preventDefault();

      const dx = event.clientX - this.dragState.x;
      const dy = event.clientY - this.dragState.y;

      // Set click prevention if movement exceeds threshold
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
        this.preventClick = true;
      }

      this.slider.scrollTo({
        left: this.dragState.left - dx,
        behavior: 'instant',
      });
    };

    onPointerUp = (event) => {
      if (!this.dragState.isDragging) return;

      this.dragState.isDragging = false;

      const dx = event.clientX - this.dragState.x;
      const elapsed = Date.now() - this.dragState.startTime;
      const velocity = Math.abs(dx) / elapsed;

      // If we have significant velocity or displacement, move to the next/previous slide
      if (Math.abs(dx) > 50 || velocity > 0.5) {
        if (dx > 0) {
          this.previous();
        } else {
          this.next();
        }
      } else {
        this.select(this.activeIndex);
      }

      this.dispatchEvent(new CustomEvent('mx-slider:drag-end'));

      this.events.remove(document, 'pointermove', this.onPointerMove);
      this.events.remove(document, 'pointerup', this.onPointerUp);

      setTimeout(() => {
        this.events.remove(this.slider, 'click', this.onClick, { capture: true });
      }, 100);
    };

    onClick = (event) => {
      if (this.preventClick) {
        event.preventDefault();
        event.stopPropagation();
        this.preventClick = false;
      }
    };
  };

  if (!window.customElements.get('mx-slider')) {
    window.customElements.define('mx-slider', window.mx.components.MxSlider);
  }

  window.mx.components.MxImageZoom = class extends window.mx.components.MxComponent {
    constructor() {
      super();

      this.isZoomed = false;
      this.initialScale = 1;
      this.currentScale = 1;
      this.maxScale = 3;
      this.lastPinchDistance = 0;

      this.panState = {
        isPanning: false,
        startX: 0,
        startY: 0,
        translateX: 0,
        translateY: 0,
      };
    }

    connectedCallback() {
      if (!this.image) return;

      this.events.add(this.image, 'wheel', this.onWheel, { passive: false });
      this.events.add(this.image, 'dblclick', this.onDoubleClick);

      this.events.add(this.image, 'pointerdown', this.onPointerDown);

      if ('GestureEvent' in window) {
        this.events.add(this.image, 'gesturestart', this.onGestureStart);
        this.events.add(this.image, 'gesturechange', this.onGestureChange);
        this.events.add(this.image, 'gestureend', this.onGestureEnd);
      }

      if (this.zoomButton) {
        this.events.add(this.zoomButton, 'click', this.onZoomButtonClick);
      }
    }

    get image() {
      return this.querySelector('img');
    }
    get zoomButton() {
      return this.closest('mx-modal-manager').querySelector('.zoom-button');
    }

    toggleZoom = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (this.isZoomed) {
        this.resetZoom();
      } else {
        this.zoomToPoint(
          event ? event.clientX : this.image.offsetWidth / 2,
          event ? event.clientY : this.image.offsetHeight / 2,
        );
      }

      return this.isZoomed;
    };

    zoomToPoint = (clientX, clientY) => {
      if (!this.image) return;

      const rect = this.image.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const offsetY = clientY - rect.top;

      const percentX = offsetX / rect.width;
      const percentY = offsetY / rect.height;

      this.image.style.transformOrigin = `${percentX * 100}% ${percentY * 100}%`;

      this.currentScale = this.maxScale;
      this.image.style.transform = `scale(${this.currentScale})`;

      this.panState.translateX = 0;
      this.panState.translateY = 0;

      this.image.classList.add('zoomed');
      this.isZoomed = true;

      const slider = this.closest('mx-slider');
      if (slider) {
        slider.setAttribute('data-zoom-active', '');
      }

      this.dispatchEvent(
        new CustomEvent('mx-image-zoom:zoom', {
          bubbles: true,
          detail: { scale: this.currentScale },
        }),
      );
    };

    resetZoom = () => {
      if (!this.image) return;

      this.currentScale = this.initialScale;
      this.image.style.transform = '';
      this.image.style.transformOrigin = '';
      this.image.classList.remove('zoomed');
      this.isZoomed = false;

      const slider = this.closest('mx-slider');
      if (slider) {
        slider.removeAttribute('data-zoom-active');
      }

      this.dispatchEvent(
        new CustomEvent('mx-image-zoom:reset', {
          bubbles: true,
          detail: { scale: this.currentScale },
        }),
      );
    };

    onZoomButtonClick = (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      const slider = this.closest('mx-slider');
      if (!slider) return;

      if (slider.slideList[slider.activeIndex] !== this.closest('.gallery-modal-slide')) {
        return;
      }

      if (this.isZoomed) {
        this.resetZoom();
      } else {
        const rect = this.image.getBoundingClientRect();
        this.zoomToPoint(rect.left + rect.width / 2, rect.top + rect.height / 2);
      }
    };

    onWheel = (event) => {
      if (!this.image) return;

      event.preventDefault();

      const delta = -Math.sign(event.deltaY);
      const scaleFactor = 0.1;

      if (delta > 0 && this.currentScale < this.maxScale) {
        this.currentScale = Math.min(this.currentScale + scaleFactor, this.maxScale);
      } else if (delta < 0 && this.currentScale > this.initialScale) {
        this.currentScale = Math.max(this.currentScale - scaleFactor, this.initialScale);

        if (this.currentScale <= this.initialScale) {
          this.resetZoom();
          return;
        }
      } else {
        return;
      }

      const rect = this.image.getBoundingClientRect();
      const offsetX = event.clientX - rect.left;
      const offsetY = event.clientY - rect.top;

      const percentX = offsetX / rect.width;
      const percentY = offsetY / rect.height;

      this.image.style.transformOrigin = `${percentX * 100}% ${percentY * 100}%`;

      this.image.style.transform = `scale(${this.currentScale})
                                   translate(${this.panState.translateX}px, ${this.panState.translateY}px)`;

      if (this.currentScale > this.initialScale && !this.isZoomed) {
        this.isZoomed = true;
        this.image.classList.add('zoomed');

        const slider = this.closest('mx-slider');
        if (slider) {
          slider.setAttribute('data-zoom-active', '');
        }
      }
    };

    onDoubleClick = (event) => {
      this.toggleZoom(event);
    };

    onPointerDown = (event) => {
      if (!this.isZoomed) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      this.panState.isPanning = true;
      this.panState.startX = event.clientX - this.panState.translateX;
      this.panState.startY = event.clientY - this.panState.translateY;

      document.addEventListener('pointermove', this.onPointerMove, { passive: false });
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerUp);

      if (event.pointerType === 'touch') {
        this.image.setPointerCapture(event.pointerId);
      }
    };

    onPointerMove = (event) => {
      if (!this.panState.isPanning || !this.isZoomed) return;

      event.preventDefault();

      const newTranslateX = event.clientX - this.panState.startX;
      const newTranslateY = event.clientY - this.panState.startY;

      const rect = this.image.getBoundingClientRect();
      const scaledWidth = rect.width * this.currentScale;
      const scaledHeight = rect.height * this.currentScale;

      const maxTranslateX = (scaledWidth - rect.width) / 2 / this.currentScale;
      const maxTranslateY = (scaledHeight - rect.height) / 2 / this.currentScale;

      this.panState.translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, newTranslateX));
      this.panState.translateY = Math.max(-maxTranslateY, Math.min(maxTranslateY, newTranslateY));

      this.image.style.transform = `scale(${this.currentScale})
                                   translate(${this.panState.translateX}px, ${this.panState.translateY}px)`;
    };

    onPointerUp = (event) => {
      if (!this.panState.isPanning) return;

      this.panState.isPanning = false;

      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointerup', this.onPointerUp);
      document.removeEventListener('pointercancel', this.onPointerUp);

      if (event.pointerType === 'touch' && this.image.hasPointerCapture(event.pointerId)) {
        this.image.releasePointerCapture(event.pointerId);
      }
    };

    onGestureStart = (event) => {
      event.preventDefault();
      this.initialScale = this.currentScale;
    };

    onGestureChange = (event) => {
      event.preventDefault();

      if (!this.image) return;

      const newScale = this.initialScale * event.scale;

      this.currentScale = Math.max(this.initialScale, Math.min(this.maxScale, newScale));

      this.image.style.transform = `scale(${this.currentScale})
                                   translate(${this.panState.translateX}px, ${this.panState.translateY}px)`;

      if (this.currentScale > this.initialScale && !this.isZoomed) {
        this.isZoomed = true;
        this.image.classList.add('zoomed');

        const slider = this.closest('mx-slider');
        if (slider) {
          slider.setAttribute('data-zoom-active', '');
        }
      }
    };

    onGestureEnd = (event) => {
      event.preventDefault();

      if (this.currentScale <= this.initialScale) {
        this.resetZoom();
      }
    };
  };
  if (!window.customElements.get('mx-image-zoom')) {
    window.customElements.define('mx-image-zoom', window.mx.components.MxImageZoom);
  }

  window.mx.components.MxAnnouncementBar = class extends window.mx.components.MxComponent {
    constructor() {
      super();
    }

    connectedCallback() {
      this.updateLayout();
    }

    get cloneCount() {
      return Array.from(this.children).filter((group) => !group.hasAttribute('data-original')).length - 1;
    }
    get originalGroup() {
      return this.cache.get('dom', 'originalGroup', () => this.querySelector('[data-original]'));
    }

    updateLayout = () => {
      this.setCloneCount(0);

      const targetCloneCount = Math.ceil((1.2 * this.offsetWidth) / this.originalGroup.offsetWidth) + 1;

      if (this.cloneCount !== targetCloneCount) {
        this.modifiers.remove('running');
        this.setCloneCount(targetCloneCount);
      }

      if (!this.modifiers.has('running')) {
        this.modifiers.add('running');
      }
    };

    setCloneCount = (count) => {
      const clones = this.cloneCount;

      if (clones > count) {
        for (let i = 0; i < clones - count; i++) {
          this.removeChild(this.lastElementChild);
        }
      } else if (clones < count) {
        for (let i = 0; i < count - clones; i++) {
          const clone = this.originalGroup.cloneNode(true);
          clone.removeAttribute('data-original');
          this.appendChild(clone);
        }
      }
    };

    onResize = () => {
      this.updateLayout();
    };
  };
  if (!customElements.get('mx-announcement-bar')) {
    customElements.define('mx-announcement-bar', window.mx.components.MxAnnouncementBar);
  }

  window.mx.components.MxLocalizationTooltip = class extends window.mx.components.MxComponent {
    constructor() {
      super();
    }

    connectedCallback() {
      this.events.add(this.languageLabel, 'click', this.onLanguageLabelClick);

      this.languageList.forEach((language) => {
        this.events.add(language, 'click', this.onLanguageClick);
      });

      this.events.add(this, 'mx-localization-tooltip:open', () => {
        this.events.add(document, 'click', this.onDocumentClick);
        this.events.add(window, 'scroll', window.mx.utils.throttle(this.onWindowScroll), { passive: true });
      });

      this.events.add(this, 'mx-localization-tooltip:close', () => {
        this.events.remove(document, 'click', this.onDocumentClick);
        this.events.remove(window, 'scroll', window.mx.utils.throttle(this.onWindowScroll));
      });
    }

    get languageLabel() {
      return this.cache.get('dom', 'languageLabel', () => {
        return this.parentElement.querySelector('.language-label');
      });
    }

    get languageList() {
      return this.cache.get('dom', 'languageList', () => {
        return Array.from(this.querySelectorAll('span'));
      });
    }

    onLanguageLabelClick = () => {
      this.modifiers.has('open') ? this.close() : this.open();
    };

    onLanguageClick = (event) => {
      this.modifiers.add('disabled');

      const targetUrl = event.currentTarget.getAttribute('data-url');
      window.location.href = targetUrl;

      this.modifiers.remove('disabled');
    };

    onDocumentClick = (event) => {
      if (!this.modifiers.has('open')) return;

      if (!this.contains(event.target) && !event.target.closest('.language-label')) {
        this.close();
      }
    };

    onWindowScroll = () => {
      if (this.modifiers.has('open')) this.close();
    };

    open = () => {
      this.modifiers.add('open');

      this.dispatchEvent(
        new CustomEvent('mx-localization-tooltip:open', {
          bubbles: true,
          detail: { tooltip: this },
        }),
      );
    };

    close = () => {
      this.modifiers.remove('open');

      this.dispatchEvent(
        new CustomEvent('mx-localization-tooltip:close', {
          bubbles: true,
          detail: { tooltip: this },
        }),
      );
    };
  };

  if (!window.customElements.get('mx-localization-tooltip')) {
    window.customElements.define('mx-localization-tooltip', window.mx.components.MxLocalizationTooltip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.mx.init);
  } else {
    window.mx.init();
  }

  /**
   * Initialize scrollbar width tracking
   */
  function initScrollbarWidthTracking() {
    window.mx.utils.setScrollbarWidthProperty();
    new ResizeObserver(
      window.mx.utils.throttle(() => {
        window.mx.utils.setScrollbarWidthProperty();
      }),
    ).observe(document.body);
  }

  /**
   * Initialize Gorgias integration
   * - Updates Gorgias chat visibility when drawers and modals are opened/closed
   * - Updates Gorgias chat visibility when the page is scrolled
   */
  function initGorgiasIntegration() {
    const checkAndInitialize = () => {
      if (window.mx.utils.gorgias.getContainer() || window.GorgiasChat) {
        document.addEventListener('mx-drawer:open', () => {
          window.mx.utils.gorgias.hide();
        });

        document.addEventListener('mx-drawer:close', () => {
          window.mx.utils.gorgias.show();
        });

        document.addEventListener('mx-modal:open', () => {
          window.mx.utils.gorgias.hide();
        });

        document.addEventListener('mx-modal:close', () => {
          window.mx.utils.gorgias.show();
        });

        const footer = window.mx.dom.footer;

        if (footer && footer.observers) {
          footer.observers.intersection(footer, (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                window.mx.utils.gorgias.hide();
              } else {
                window.mx.utils.gorgias.show();
              }
            });
          });
        } else {
          console.warn('[gorgiasIntegration] Footer not found');
        }

        return true;
      } else {
        return false;
      }
    };

    // Try immediate initialization
    if (checkAndInitialize()) return;

    // Set up retry mechanism - try for up to 10 seconds
    let attempts = 0;
    const maxAttempts = 10;
    const retryInterval = setInterval(() => {
      attempts++;

      if (checkAndInitialize() || attempts >= maxAttempts) {
        clearInterval(retryInterval);

        if (attempts >= maxAttempts) {
          console.warn('[gorgiasIntegration] Gorgias chat not found after maximum retry attempts');
        }
      }
    }, 1000);
  }
})();
